/**
 * Saude da empresa: o selo curto (lista, cabecalho) e o cartao completo (aba
 * Resumo), com nota, achados e os passos de implantacao.
 *
 * Decisoes nao obvias:
 *  - A nota e um anel com o numero no meio, mas o NIVEL escrito e quem manda:
 *    cor sozinha nunca comunica estado, e "72" nao diz nada sem "Atenção".
 *  - Cada achado que tem onde ser resolvido ganha um botao que leva direto a
 *    aba certa do detalhe (`onIrPara`). Diagnostico sem caminho e so alarme.
 *  - Os passos de implantacao aparecem para TODA empresa, nao so para as
 *    novas: uma conta ativa sem vendedora cadastrada ainda tem um passo em
 *    aberto, e e ali que a equipe descobre.
 */

import { Badge, Button, Icon, ProgressBar, SectionCard, type BadgeTone } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import { achadoPrincipal } from '../saude';
import type { AbaDetalhe, NivelSaude, SaudeDaEmpresa, TomDoAchado } from '../types';
import { ABAS_DETALHE, NIVEL_SAUDE } from '../vocabulario';

const ANEL: Readonly<Record<NivelSaude, string>> = {
  saudavel: 'stroke-status-good',
  atencao: 'stroke-status-warning',
  critica: 'stroke-status-critical',
  implantacao: 'stroke-info',
  pausada: 'stroke-ink-subtle',
};

const TOM_ACHADO: Readonly<Record<TomDoAchado, { readonly cor: string; readonly icon: 'alerta' | 'info' | 'check'; readonly rotulo: string; readonly badge: BadgeTone }>> = {
  critical: { cor: 'text-status-critical', icon: 'alerta', rotulo: 'Crítico', badge: 'critical' },
  warning: { cor: 'text-status-warning', icon: 'alerta', rotulo: 'Atenção', badge: 'warning' },
  neutral: { cor: 'text-info', icon: 'info', rotulo: 'Oportunidade', badge: 'info' },
  good: { cor: 'text-status-good', icon: 'check', rotulo: 'Boa notícia', badge: 'good' },
};

function rotuloDaAba(aba: AbaDetalhe): string {
  return ABAS_DETALHE.find((a) => a.value === aba)?.label ?? aba;
}

/** Selo curto com o nivel; o `title` traz o motivo principal em termos de negocio. */
export function SeloDeSaude({ saude, className = '' }: { readonly saude: SaudeDaEmpresa | null; readonly className?: string }): JSX.Element {
  if (saude === null) {
    return (
      <span title="A visão geral das empresas não carregou; a saúde depende dela." className={className}>
        <Badge tone="neutral">Sem dados</Badge>
      </span>
    );
  }
  const info = NIVEL_SAUDE[saude.nivel];
  const motivo = achadoPrincipal(saude);
  return (
    <span title={motivo === null ? info.explica : motivo.texto} className={className}>
      <Badge tone={info.tone} icon={info.icon}>
        {info.label}
      </Badge>
    </span>
  );
}

function Anel({ saude }: { readonly saude: SaudeDaEmpresa }): JSX.Element {
  const R = 40;
  const C = 2 * Math.PI * R;
  const preenchido = (Math.max(0, Math.min(100, saude.nota)) / 100) * C;
  const info = NIVEL_SAUDE[saude.nivel];
  return (
    <svg viewBox="0 0 100 100" className="h-28 w-28 shrink-0" role="img" aria-label={`Nota de saúde ${saude.nota} de 100: ${info.label}`}>
      <circle cx={50} cy={50} r={R} className="stroke-ink/10" strokeWidth={9} fill="none" />
      <circle
        cx={50}
        cy={50}
        r={R}
        className={ANEL[saude.nivel]}
        strokeWidth={9}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={`${preenchido} ${C - preenchido}`}
        transform="rotate(-90 50 50)"
      />
      <text x={50} y={50} textAnchor="middle" className="fill-ink" fontSize={22} fontWeight={600}>
        {saude.nota}
      </text>
      <text x={50} y={64} textAnchor="middle" className="fill-ink-subtle" fontSize={8}>
        de 100
      </text>
    </svg>
  );
}

export function CartaoDeSaude({
  saude,
  onIrPara,
  className = '',
}: {
  readonly saude: SaudeDaEmpresa;
  readonly onIrPara: (aba: AbaDetalhe) => void;
  readonly className?: string;
}): JSX.Element {
  const info = NIVEL_SAUDE[saude.nivel];
  const feitos = saude.passos.filter((p) => p.feito).length;
  const pendentes = saude.passos.filter((p) => !p.feito);

  return (
    <SectionCard
      id="resumo-saude"
      title="Saúde da empresa"
      icon="escudo"
      description="Integrações, canais e resultado do período num diagnóstico só."
      aside={
        <Badge tone={info.tone} icon={info.icon}>
          {info.label}
        </Badge>
      }
      className={className}
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <Anel saude={saude} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink">{info.explica}</p>
            <p className="mt-1 text-xs text-ink-subtle">
              A nota desconta 30 pontos por problema crítico e 12 por ponto de atenção. Oportunidades não descontam.
            </p>
          </div>
        </div>

        <div>
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">O que a saúde encontrou</h3>
          {saude.achados.length === 0 ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-ink-muted">
              <Icon name="check" size="sm" className="text-status-good" />
              Nada a apontar: integrações em dia e régua trazendo vendas de volta.
            </p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {saude.achados.map((achado) => {
                const tom = TOM_ACHADO[achado.tom];
                return (
                  <li key={achado.id} className="flex items-start gap-2.5 rounded-lg border border-line bg-elevated px-3 py-2.5">
                    <Icon name={tom.icon} size="sm" className={`mt-0.5 ${tom.cor}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-ink">
                        <span className="sr-only">{tom.rotulo}: </span>
                        {achado.texto}
                      </p>
                      {achado.aba === null ? null : (
                        <Button variant="ghost" size="sm" iconRight="seta-dir" className="-ml-3 mt-1" onClick={() => onIrPara(achado.aba ?? 'resumo')}>
                          Ir para {rotuloDaAba(achado.aba)}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Prontidão da régua</h3>
            <span className="text-xs tabular-nums text-ink-muted">
              {formatCount(feitos)} de {formatCount(saude.passos.length)} passos
            </span>
          </div>
          <ProgressBar value={feitos} max={saude.passos.length} tone={pendentes.length === 0 ? 'good' : 'info'} label="Passos de implantação concluídos" className="mt-2" />
          <ol className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {saude.passos.map((passo, i) => (
              <li key={passo.id} className="flex items-start gap-2 text-xs">
                <span
                  aria-hidden="true"
                  className={`mt-px grid h-4 w-4 shrink-0 place-items-center rounded-full text-[9px] font-semibold ${passo.feito ? 'bg-status-good text-white' : 'bg-ink/10 text-ink-subtle'}`}
                >
                  {passo.feito ? <Icon name="check" size="xs" /> : i + 1}
                </span>
                <span className={passo.feito ? 'text-ink-muted' : 'text-ink'}>
                  <span className="sr-only">{passo.feito ? 'Concluído: ' : 'Pendente: '}</span>
                  {passo.rotulo}
                  {passo.feito ? null : (
                    <>
                      {' '}
                      <button type="button" onClick={() => onIrPara(passo.aba)} className="font-medium text-brand underline-offset-2 hover:underline">
                        resolver<span className="sr-only">: {passo.rotulo}</span>
                      </button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </SectionCard>
  );
}
